import { createStore, useStore } from './store';
import { toast } from './toast';

// Copying and sharing links reliably on phones as well as desktops.
//  1. navigator.clipboard (needs HTTPS and a user gesture; can still be refused on iOS).
//  2. execCommand('copy') on a selected element, done the way iOS WebKit accepts.
//  3. A dialog with the text selected so the person can copy it by hand.

function legacyCopy(text: string): boolean {
  const area = document.createElement('textarea');
  area.value = text;
  area.setAttribute('readonly', '');
  // Off-screen but still "visible" to the selection APIs; 16px avoids iOS zooming in.
  Object.assign(area.style, { position: 'fixed', top: '0', left: '-9999px', fontSize: '16px', opacity: '0' });
  document.body.appendChild(area);
  const previous = document.activeElement as HTMLElement | null;
  try {
    area.focus();
    area.select();
    area.setSelectionRange(0, text.length); // select() alone does nothing on iOS
    return document.execCommand('copy');
  } catch {
    return false;
  } finally {
    area.remove();
    previous?.focus?.();
  }
}

export async function copyToClipboard(text: string): Promise<boolean> {
  if (navigator.clipboard?.writeText && window.isSecureContext) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      /* fall through to the legacy path */
    }
  }
  return legacyCopy(text);
}

// ── Manual fallback dialog ──────────────────────────────────────────────

export type CopyWhat = 'enlace' | 'invitación' | 'dirección';

const DONE: Record<CopyWhat, string> = { enlace: 'Enlace copiado', 'invitación': 'Invitación copiada', 'dirección': 'Dirección copiada' };

export interface ManualCopyRequest {
  text: string;
  label: CopyWhat;
}

export const manualCopyStore = createStore<{ request: ManualCopyRequest | null }>({ request: null });

export function useManualCopy(): ManualCopyRequest | null {
  return useStore(manualCopyStore, (s) => s.request);
}

export function closeManualCopy(): void {
  manualCopyStore.set({ request: null });
}

/** Copies and confirms with a notice; if the browser refuses, shows the text to copy by hand. */
export async function copyWithFeedback(text: string, label: CopyWhat): Promise<void> {
  if (await copyToClipboard(text)) {
    toast(DONE[label], 'success');
  } else {
    manualCopyStore.set({ request: { text, label } });
  }
}

// ── Native share sheet ──────────────────────────────────────────────────

/** Phones and tablets get the system share sheet (WhatsApp, Mensajes, Copiar…). */
export function canShareNatively(): boolean {
  return typeof navigator.share === 'function' && window.matchMedia('(pointer: coarse)').matches;
}

/**
 * Opens the share sheet when available; otherwise copies `url`.
 * Cancelling the sheet is not an error and does nothing.
 */
export async function shareLink(opts: { title: string; text: string; url: string }): Promise<void> {
  if (canShareNatively()) {
    try {
      await navigator.share(opts);
      return;
    } catch (err) {
      if ((err as DOMException).name === 'AbortError') return;
      /* share sheet unavailable: copy instead */
    }
  }
  await copyWithFeedback(opts.url, 'enlace');
}
